import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Check, ShoppingBag, ArrowLeft, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../context/CartContext';
import emailjs from '@emailjs/browser';

interface OrderSuccessPageProps {}

export default function OrderSuccessPage({}: OrderSuccessPageProps) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [emailSent, setEmailSent] = useState(false);
  const emailSentRef = useRef(false); // Use ref to prevent re-renders from affecting the check
  
  // Get order data either from location state or searchParams
  // This handles both direct navigation and ESTO payment returns
  const estoReference = searchParams.get('reference');
  const status = searchParams.get('status');
  
  const defaultOrderData = {
    orderId: estoReference || 'TK-00000',
    email: '',
    customerName: '',
    orderTotal: 0,
    paymentMethod: '',
    shippingMethod: '',
    shippingAddress: '',
    items: []
  };

  const { orderData = defaultOrderData } = location.state || { orderData: defaultOrderData };

  // Function to send order confirmation emails with robust duplicate prevention
  const sendOrderConfirmationEmail = async (orderInfo: any) => {
    try {
      // Multiple layers of duplicate prevention
      const orderIdForCheck = orderInfo.orderId || estoReference || 'unknown';
      
      // Check 1: Use ref to prevent multiple calls in the same session
      if (emailSentRef.current) {
        console.log('Emails already sent in this session (ref check), skipping...');
        return;
      }

      // Check 2: SessionStorage check to prevent sending across page reloads
      const emailSentKey = `email_sent_${orderIdForCheck}`;
      const alreadySent = sessionStorage.getItem(emailSentKey);
      
      if (alreadySent) {
        console.log('Emails already sent for this order (storage check), skipping...');
        emailSentRef.current = true;
        setEmailSent(true);
        return;
      }

      // Check 3: LocalStorage check for more persistent prevention
      const localStorageKey = `email_sent_persistent_${orderIdForCheck}`;
      const persistentCheck = localStorage.getItem(localStorageKey);
      
      if (persistentCheck) {
        console.log('Emails already sent for this order (persistent check), skipping...');
        emailSentRef.current = true;
        setEmailSent(true);
        return;
      }

      // Mark as being sent immediately to prevent race conditions
      emailSentRef.current = true;
      sessionStorage.setItem(emailSentKey, 'true');
      localStorage.setItem(localStorageKey, 'true');
      setEmailSent(true);

      console.log('Sending order confirmation emails for order:', orderIdForCheck);

      // Format order items for the email template
      const productsHtml = orderInfo.items?.map((item: any) => `
        <tr>
          <td style="padding: 12px;">
            <div style="display: flex; align-items: center;">
              <img src="${item.image || 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg'}" 
                   alt="${item.name}" 
                   class="product-image" 
                   style="width: 50px; height: 50px; object-fit: cover; border-radius: 4px; margin-right: 12px;">
              <span>${item.name}</span>
            </div>
          </td>
          <td style="padding: 12px; text-align: center;">${item.quantity}</td>
          <td style="padding: 12px; text-align: right;">${item.price?.toFixed(2) || '0.00'}€</td>
          <td style="padding: 12px; text-align: right;">${((item.price || 0) * (item.quantity || 1)).toFixed(2)}€</td>
        </tr>
      `).join('') || '';

      // Format shipping method and address for email
      let emailShippingMethod = orderInfo.shippingMethod;
      let emailShippingAddress = '';

      if (orderInfo.shippingMethod === 'Tulen ise järgi') {
        // Use user-friendly name instead of raw method
        emailShippingMethod = 'Isiklik järeletulemine';
        emailShippingAddress = ''; // No separate address needed
      } else if (orderInfo.shippingAddress) {
        // For other shipping methods, keep the method name and show the address
        emailShippingAddress = orderInfo.shippingAddress;
      }

      // Create a unique set of email addresses for this specific order send
      const emailAddressesForThisOrder = new Set<string>();
      const customerEmail = orderInfo.email?.toLowerCase()?.trim();
      const shopEmail = "ametliktouks@gmail.com".toLowerCase().trim();

      // Only add unique emails to our sending list
      if (customerEmail) {
        emailAddressesForThisOrder.add(customerEmail);
      }
      if (shopEmail && shopEmail !== customerEmail) {
        emailAddressesForThisOrder.add(shopEmail);
      }

      console.log(`Will send emails to ${emailAddressesForThisOrder.size} unique addresses:`, Array.from(emailAddressesForThisOrder));

      // Send emails sequentially to avoid race conditions
      for (const emailAddress of emailAddressesForThisOrder) {
        const isCustomerEmail = emailAddress === customerEmail;
        const targetEmail = isCustomerEmail ? orderInfo.email : "ametliktouks@gmail.com";
        const targetName = isCustomerEmail ? orderInfo.customerName : "Tõuks.ee";

        const templateParams = {
          to_name: targetName,
          to_email: targetEmail,
          reply_to: isCustomerEmail ? "ametliktouks@gmail.com" : orderInfo.email,
          order_id: orderInfo.orderId,
          customer_name: orderInfo.customerName,
          customer_email: orderInfo.email,
          customer_phone: orderInfo.customerPhone || '',
          shipping_method: emailShippingMethod,
          shipping_address: emailShippingAddress,
          payment_method: orderInfo.paymentMethod,
          products_html: productsHtml, 
          subtotal: orderInfo.orderTotal?.toFixed(2) || '0.00',
          shipping: "TASUTA",
          total: orderInfo.orderTotal?.toFixed(2) || '0.00',
          email: targetEmail
        };

        try {
          await emailjs.send(
            "service_p298pqg",
            "template_vpeq4zx",
            templateParams,
            import.meta.env.VITE_EMAILJS_PUBLIC_KEY
          );
          console.log(`Email sent successfully to: ${targetEmail} (${isCustomerEmail ? 'customer' : 'shop'})`);
          
          // Add small delay between emails to prevent rate limiting
          if (emailAddressesForThisOrder.size > 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (emailError) {
          console.error(`Failed to send email to ${targetEmail}:`, emailError);
          // Don't throw here, continue with other emails
        }
      }

      console.log("Order confirmation email process completed");
      
    } catch (error) {
      console.error("Failed to send order confirmation emails:", error);
      // Reset flags on error so user can try again if needed
      emailSentRef.current = false;
      setEmailSent(false);
      // But keep the storage flags to prevent auto-retry
    }
  };

  // Effect to handle cart clearing and email sending - runs only once per order
  useEffect(() => {
    // Prevent multiple runs with a flag
    if (emailSentRef.current) {
      return;
    }

    // Initialize EmailJS first - this must happen before any email sending
    emailjs.init("JveHxSEYo9cyKqJ74");

    if (estoReference && status === 'paid') {
      // Clear the cart when returning from ESTO payment gateway with successful payment
      clearCart();
      console.log('ESTO Payment successful, reference:', estoReference);
    }

    // Get order data from sessionStorage if available (for email sending)
    let orderDataForEmail = orderData;
    try {
      const storedOrderData = sessionStorage.getItem('orderDataForEmail');
      if (storedOrderData) {
        orderDataForEmail = JSON.parse(storedOrderData);
        // Clear the stored data after using it
        sessionStorage.removeItem('orderDataForEmail');
      }
    } catch (error) {
      console.error('Error reading order data from sessionStorage:', error);
    }

    // Send emails if we have valid order data
    if (orderDataForEmail && 
        (orderDataForEmail.orderId || estoReference) && 
        orderDataForEmail.email && 
        !emailSentRef.current) {
      
      // Use the ESTO reference if available, otherwise use the stored order ID
      if (estoReference && !orderDataForEmail.orderId) {
        orderDataForEmail.orderId = estoReference;
      }
      
      sendOrderConfirmationEmail(orderDataForEmail);
    }
  }, []); // Empty dependency array to run only once

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="container mx-auto py-20 px-6">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-8">
        <div className="rounded-full bg-green-100 p-4 w-24 h-24 mx-auto flex items-center justify-center">
          <Check className="w-12 h-12 text-green-600" />
        </div>
        
        <h1 className="text-3xl font-bold mt-6 mb-4 text-center">Tellimus edukalt esitatud!</h1>
        
        <p className="text-gray-600 mb-2 text-center">
          Täname sind tellimuse eest. Tellimuse number on: <strong>{orderData?.orderId}</strong>
        </p>
        
        {orderData?.email && (
          <p className="text-gray-600 mb-2 text-center">
            Saatsime kinnituse sinu e-postile: <strong>{orderData?.email}</strong>
          </p>
        )}
        
        <p className="text-amber-600 text-sm mb-8 text-center flex justify-center items-center">
          <Mail className="w-4 h-4 mr-1" /> Kontrolli ka rämpsposti kausta!
        </p>

        <div className="bg-gray-50 p-6 rounded-xl mb-8">
          <h2 className="text-xl font-bold mb-4">Tellimuse kokkuvõte</h2>
          
          <div className="space-y-4">
            {orderData?.customerName && (
              <div className="flex flex-col">
                <span className="text-gray-600">Klient:</span>
                <span className="font-medium">{orderData?.customerName}</span>
              </div>
            )}
            
            {orderData?.shippingMethod && (
              <div className="flex flex-col">
                <span className="text-gray-600">Transport:</span>
                <span className="font-medium">
                  {orderData?.shippingMethod === 'Tulen ise järgi' ? 'Isiklik järeletulemine' : orderData?.shippingMethod}
                </span>
                {orderData?.shippingAddress && orderData?.shippingMethod !== 'Tulen ise järgi' && (
                  <span className="text-sm text-gray-500">{orderData?.shippingAddress}</span>
                )}
              </div>
            )}
            
            {orderData?.paymentMethod && (
              <div className="flex flex-col">
                <span className="text-gray-600">Makseviis:</span>
                <span className="font-medium">{orderData?.paymentMethod}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl mb-8">
          <h2 className="text-xl font-bold mb-4">Maksmise info</h2>
          <div className="space-y-4">
            <div className="flex flex-col">
              <span className="text-gray-600">Ettevõte:</span>
              <span className="font-medium">Kvantis OÜ</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-600">Pank:</span>
              <span className="font-medium">LHV</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-600">Konto number:</span>
              <span className="font-medium">EE537700771011541337</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-600">Summa:</span>
              <span className="font-medium">{orderData?.orderTotal?.toFixed(2) || '0.00'} €</span>
            </div>
            <div className="flex flex-col border-t border-gray-200 pt-4 mt-4">
              <span className="text-gray-600">Selgitus:</span>
              <span className="font-semibold text-[#4FC1E9]">Tellimuse number {orderData?.orderId}</span>
            </div>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg mt-6 text-amber-800 text-sm">
            <p>
              <strong>NB!</strong> Palun lisa kindlasti makse selgitusse tellimuse number.
              Tellimus töödeldakse pärast makse laekumist.
            </p>
          </div>
        </div>
        
        <div className="border-t border-gray-100 pt-8 mt-8">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/"
              className="px-6 py-3 bg-[#4FC1E9] text-white rounded-full hover:bg-[#3ba8d0] transition-colors flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Tagasi avalehele
            </Link>
            
            <Link
              to="/toukeratas"
              className="px-6 py-3 border border-[#4FC1E9] text-[#4FC1E9] rounded-full hover:bg-[#4FC1E9]/10 transition-colors flex items-center justify-center"
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Jätka ostlemist
            </Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
